import { useState } from 'react';

import { useRentalManagerUserExperimentWebApiHandler_getTreatment } from '@zg-rentals/ts-rental-manager-api-client-react-query';

import { useIsomorphicLayoutEffect } from './useIsomorphicLayoutEffect';

import {
  getCookieAsBoolean,
  IA_RESTRUCTURE_SPLIT_FEATURE_NAME,
  ZRM_IA_RESTRUCTURE_ENABLED,
} from '../utils/featureFlags';

export type UseListingCentricViewEnabledParams = {
  loggedIn?: boolean;
  /** ZRAM users are always excluded from the experiment, ahead of `override`, the QA cookie and Split. */
  isEnterpriseCustomer?: boolean;
  /** Returns this value verbatim and skips both the cookie read and the Split call. */
  override?: boolean;
};

/**
 * IA restructure experiment gate, shared by the IA top nav bar, the Listings entry point and zrmw's
 * Flexible Properties page - they ship together, so one treatment drives all three. React Query
 * dedupes on the shared key, so calling this from several surfaces costs one request.
 *
 * Uses the generated ts-client hook rather than `useGetTreatment` from `@zg-rentals/split`
 * because the latter requires `SplitProvider`, which most apps consuming this module do not
 * mount. `isLoading` is for route guards - redirecting before the treatment resolves would
 * bounce every enrolled user.
 */
export const useListingCentricViewEnabled = ({
  loggedIn,
  isEnterpriseCustomer,
  override,
}: UseListingCentricViewEnabledParams = {}) => {
  // Read after mount, not during render: the cookie is false on the server and would mismatch
  // on hydration. Layout effect so QA opt-ins apply before paint, with no chrome shift - only
  // the treatment below has to resolve over the network.
  const [cookie, setCookie] = useState({ checked: false, enabled: false });

  useIsomorphicLayoutEffect(() => {
    setCookie({ checked: true, enabled: getCookieAsBoolean(ZRM_IA_RESTRUCTURE_ENABLED) });
  }, []);

  // `!cookie.enabled` keeps QA opt-ins out of the experiment's exposure data.
  const isQueryEnabled =
    override === undefined && Boolean(loggedIn) && !isEnterpriseCustomer && cookie.checked && !cookie.enabled;

  const { data, isPending } = useRentalManagerUserExperimentWebApiHandler_getTreatment(
    { featureName: IA_RESTRUCTURE_SPLIT_FEATURE_NAME },
    {
      enabled: isQueryEnabled,
      staleTime: Infinity,
      throwOnError: false,
      meta: {
        tags: ['ia-restructure-treatment'],
        context: { api: 'getTreatment', details: { featureName: IA_RESTRUCTURE_SPLIT_FEATURE_NAME } },
      },
    },
  );

  if (isEnterpriseCustomer) {
    return { isEnabled: false, isLoading: false };
  }

  if (override !== undefined) {
    return { isEnabled: override, isLoading: false };
  }

  return {
    isEnabled: cookie.enabled || data?.treatment === 'on',
    isLoading: Boolean(loggedIn) && (!cookie.checked || (isQueryEnabled && isPending)),
  };
};

export default useListingCentricViewEnabled;
